
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { PainelSuccessDialog } from '@/components/painel/PainelFeedbackDialog';

export type PainelSaveSuccessOptions = {
  title?: string;
  description?: string;
  /** Executado ao fechar o modal (botão OK ou clique fora). */
  onClose?: () => void;
};

type PainelSaveFeedbackContextValue = {
  showSaveSuccess: (options?: PainelSaveSuccessOptions) => void;
};

const PainelSaveFeedbackContext = createContext<PainelSaveFeedbackContextValue | null>(null);

export function PainelSaveFeedbackProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('Alterações guardadas');
  const [description, setDescription] = useState(
    'As suas alterações foram guardadas com sucesso.'
  );
  const onCloseRef = useRef<(() => void) | undefined>(undefined);

  const showSaveSuccess = useCallback((options?: PainelSaveSuccessOptions) => {
    setTitle(options?.title ?? 'Alterações guardadas');
    setDescription(
      options?.description ?? 'As suas alterações foram guardadas com sucesso.'
    );
    onCloseRef.current = options?.onClose;
    setOpen(true);
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const cb = onCloseRef.current;
      onCloseRef.current = undefined;
      setOpen(false);
      cb?.();
      return;
    }
    setOpen(true);
  };

  return (
    <PainelSaveFeedbackContext.Provider value={{ showSaveSuccess }}>
      {children}
      <PainelSuccessDialog
        open={open}
        onOpenChange={handleOpenChange}
        title={title}
        description={description}
      />
    </PainelSaveFeedbackContext.Provider>
  );
}

export function usePainelSaveFeedback() {
  const ctx = useContext(PainelSaveFeedbackContext);
  if (!ctx) {
    throw new Error('usePainelSaveFeedback must be used within PainelSaveFeedbackProvider');
  }
  return ctx;
}
